import React, { useState, useRef, useEffect } from 'react';
import { Target, Download, RotateCcw, Plus, Pencil, Check, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { formatNumber } from '../utils';

export function PlanHeader({
  entriesCount,
  isDownloading,
  onDownload,
  onReset,
  canUndo,
  onUndo,
  capitalInputValue,
  onCapitalChange,
  hasUnsavedCapital,
  onSaveCapital,
  accountStats,
  onOpenDeposits,
  accountAdjustment = 0,
  onEditTotal,
  onResetAdjustment
}) {
  const { t, language } = useLanguage();
  const [editingTotal, setEditingTotal] = useState(false);
  const [totalInput, setTotalInput] = useState('');
  const statsRef = useRef(null);
  const [showStickyBar, setShowStickyBar] = useState(false);

  // Compact fixed bar appears when the stats cards scroll out of view
  useEffect(() => {
    const el = statsRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { rootMargin: '-60px 0px 0px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const startEditTotal = () => {
    setTotalInput(accountStats.totalAccount.toFixed(2));
    setEditingTotal(true);
  };

  const saveTotal = () => {
    const value = Number(totalInput);
    if (!isNaN(value) && onEditTotal) onEditTotal(value);
    setEditingTotal(false);
  };

  return (
    <div className="w-full space-y-4">
      {/* Compact fixed info bar while scrolling */}
      {showStickyBar && (
        <div className="fixed top-11 sm:top-12 left-0 right-0 z-30 px-2 sm:px-4 pointer-events-none" data-testid="plan-sticky-bar">
          <div className="max-w-7xl mx-auto bg-white/95 backdrop-blur-md border border-gray-200 shadow-lg rounded-xl px-3 sm:px-4 py-2 flex items-center justify-between gap-3 overflow-x-auto no-scrollbar pointer-events-auto">
            <span className="text-[11px] sm:text-sm font-bold text-amber-700 whitespace-nowrap">
              {language === 'es' ? 'Capital:' : 'Capital:'} ${formatNumber(Number(capitalInputValue) || 0)}
            </span>
            <span className="text-[11px] sm:text-sm font-bold text-emerald-700 whitespace-nowrap" data-testid="sticky-total-account">
              {language === 'es' ? 'Total Cuenta:' : 'Total Account:'} ${formatNumber(accountStats.totalAccount)}
            </span>
            <span className="text-[11px] sm:text-sm font-bold text-blue-700 whitespace-nowrap">
              {language === 'es' ? 'Disponible:' : 'Available:'} ${formatNumber(accountStats.availableBalance)}
            </span>
            <span className="text-[11px] sm:text-sm font-bold text-purple-700 whitespace-nowrap">
              {language === 'es' ? 'Extra:' : 'Extra:'} ${formatNumber(accountStats.totalDeposits || 0)}
            </span>
          </div>
        </div>
      )}

      {/* Title + Action Buttons */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Target className="text-indigo-600" /> {t.plan.title}
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={onDownload}
            disabled={entriesCount === 0 || isDownloading}
            className={`flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-bold rounded-lg shadow-md transition-all transform active:scale-95 ${
              isDownloading 
                ? 'bg-gradient-to-br from-emerald-100 to-emerald-200 text-emerald-700 cursor-wait' 
                : entriesCount === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                  : 'bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white hover:shadow-lg'
            }`}
            title={language === 'es' ? 'Descargar archivo CSV del plan' : 'Download plan CSV file'}
            data-testid="download-plan-csv-btn"
          >
            <Download size={16} className={`sm:w-[18px] sm:h-[18px] ${isDownloading ? 'animate-bounce' : ''}`} />
            <span>
              {isDownloading 
                ? (language === 'es' ? 'Descargando...' : 'Downloading...') 
                : (language === 'es' ? 'Descargar CSV' : 'Download CSV')}
            </span>
          </button>
          <button 
            onClick={onReset}
            title={t.plan.resetPlan}
            className="flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-bold text-white bg-gradient-to-br from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 rounded-lg shadow-md hover:shadow-lg transition-all transform active:scale-95"
            data-testid="reset-plan-btn"
          >
            <RotateCcw size={16} className="sm:w-[18px] sm:h-[18px]" />
            <span>{language === 'es' ? 'Reiniciar' : 'Reset'}</span>
          </button>
          {canUndo && (
            <button 
              onClick={onUndo}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-amber-700 bg-gradient-to-br from-amber-50 to-amber-100 hover:from-amber-100 hover:to-amber-200 rounded-lg border border-amber-200 transition-all shadow-sm hover:shadow-md active:scale-95"
              data-testid="undo-reset-btn"
            >
              <RotateCcw size={16} />
              {language === 'es' ? 'Deshacer' : t.plan.undoDelete}
            </button>
          )}
        </div>
      </div>

      {/* Symmetric Stats Cards: Capital Inicial | Total Cuenta | Disponible | + Capital Extra */}
      <div ref={statsRef} className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="plan-stats-grid">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex flex-col items-center justify-center text-center min-h-[80px]">
          <span className="text-[11px] sm:text-xs font-bold text-amber-700 uppercase tracking-wide mb-1.5">
            {language === 'es' ? 'Capital Inicial' : 'Initial Capital'}
          </span>
          <div className="flex items-center gap-1.5">
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-400 font-bold">$</span>
              <input 
                type="text"
                inputMode="decimal"
                value={capitalInputValue || ''} 
                onChange={(e) => onCapitalChange(e.target.value.replace(/[^0-9.]/g, ''))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onSaveCapital();
                  }
                }}
                className="w-24 sm:w-28 pl-6 pr-2 py-1.5 bg-white border border-amber-300 rounded-lg font-bold text-amber-700 focus:ring-2 focus:ring-amber-500 outline-none transition-all text-sm"
                placeholder="0.00"
                data-testid="initial-capital-input"
              />
            </div>
            {hasUnsavedCapital && (
              <button
                onClick={onSaveCapital}
                className="px-2 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-lg transition-colors"
                data-testid="save-capital-btn"
              >
                💾
              </button>
            )}
          </div>
        </div>
        
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex flex-col items-center justify-center text-center min-h-[80px]">
          <span className="text-[11px] sm:text-xs font-bold text-emerald-700 uppercase tracking-wide mb-1">
            {language === 'es' ? 'Total Cuenta' : 'Total Account'}
          </span>
          {editingTotal ? (
            <div className="flex items-center gap-1">
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-emerald-400 font-bold text-sm">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  autoFocus
                  value={totalInput}
                  onChange={(e) => setTotalInput(e.target.value.replace(/[^0-9.-]/g, ''))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveTotal();
                    if (e.key === 'Escape') setEditingTotal(false);
                  }}
                  className="w-24 sm:w-28 pl-6 pr-2 py-1.5 bg-white border border-emerald-300 rounded-lg font-bold text-emerald-700 focus:ring-2 focus:ring-emerald-500 outline-none text-sm"
                  data-testid="total-account-input"
                />
              </div>
              <button
                onClick={saveTotal}
                className="p-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                data-testid="save-total-account-btn"
                title={language === 'es' ? 'Guardar' : 'Save'}
              >
                <Check size={14} />
              </button>
              <button
                onClick={() => setEditingTotal(false)}
                className="p-1.5 bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-lg transition-colors"
                data-testid="cancel-total-account-btn"
                title={language === 'es' ? 'Cancelar' : 'Cancel'}
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-emerald-700 text-base sm:text-lg" data-testid="total-account-value">
                ${formatNumber(accountStats.totalAccount)}
              </span>
              <button
                onClick={startEditTotal}
                className="p-1 text-emerald-400 hover:text-emerald-700 hover:bg-emerald-100 rounded-md transition-colors"
                title={language === 'es' ? 'Modificar el Total Cuenta' : 'Edit Total Account'}
                data-testid="edit-total-account-btn"
              >
                <Pencil size={14} />
              </button>
            </div>
          )}
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex flex-col items-center justify-center text-center min-h-[80px]">
          <span className="text-[11px] sm:text-xs font-bold text-blue-700 uppercase tracking-wide mb-1">
            {language === 'es' ? 'Disponible' : 'Available'}
          </span>
          <span className="font-bold text-blue-700 text-base sm:text-lg" data-testid="available-balance-value">
            ${formatNumber(accountStats.availableBalance)}
          </span>
        </div>
        
        <button
          onClick={onOpenDeposits}
          className="bg-purple-50 border border-purple-200 hover:bg-purple-100 rounded-xl p-3 flex flex-col items-center justify-center text-center min-h-[80px] transition-colors active:scale-95"
          title={language === 'es' ? 'Agregar capital extra a la cuenta' : 'Add extra capital to the account'}
          data-testid="add-extra-balance-btn"
        >
          <span className="text-[11px] sm:text-xs font-bold text-purple-700 uppercase tracking-wide mb-1 flex items-center gap-1">
            <Plus size={13} /> {language === 'es' ? 'Capital Extra' : 'Extra Capital'}
          </span>
          <span className="font-bold text-purple-700 text-base sm:text-lg" data-testid="extra-capital-value">
            {accountStats.totalDeposits > 0 && `$${formatNumber(accountStats.totalDeposits)}`}
          </span>
        </button>
      </div>

      {/* Secondary chips */}
      <div className="flex flex-wrap gap-3">
        {accountStats.totalExtracted > 0 && (
          <div className="flex items-center gap-2 bg-indigo-50 px-3 sm:px-4 py-2 rounded-lg border border-indigo-200" data-testid="total-extracted-card">
            <span className="text-xs sm:text-sm font-bold text-indigo-700 uppercase whitespace-nowrap">
              {language === 'es' ? 'Retirado:' : 'Extracted:'}
            </span>
            <span className="font-bold text-indigo-700 text-sm">
              ${formatNumber(accountStats.totalExtracted)}
            </span>
          </div>
        )}
        {accountAdjustment !== 0 && (
          <div className="flex items-center gap-2 bg-emerald-50 px-3 sm:px-4 py-2 rounded-lg border border-emerald-200" data-testid="account-adjustment-chip">
            <span className="text-xs sm:text-sm font-bold text-emerald-700 uppercase whitespace-nowrap">
              {language === 'es' ? 'Ajuste manual:' : 'Manual adjustment:'}
            </span>
            <span className={`font-bold text-sm ${accountAdjustment > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
              {accountAdjustment > 0 ? '+' : ''}${formatNumber(accountAdjustment)}
            </span>
            <button
              onClick={onResetAdjustment}
              className="text-[10px] sm:text-xs font-bold text-emerald-600 hover:text-emerald-800 underline"
              title={language === 'es' ? 'Quitar ajuste y volver al valor calculado' : 'Remove adjustment and restore calculated value'}
              data-testid="reset-adjustment-btn"
            >
              {language === 'es' ? 'Quitar' : 'Remove'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
